import { useGet } from "../api/hooks";
import type { Schema } from "../api/types";

export type InboxItem = Schema<"AppNotification">;
export interface Inbox {
  items: InboxItem[];
  unread: number;
}

/** The caller's inbox, refreshed every minute (the badge of the navigation reads the same query). */
export function useInbox(limit = 50, unreadOnly = false) {
  return useGet<Inbox>(
    "/api/v1/inbox",
    { limit, ...(unreadOnly ? { unread: true } : {}) },
    { refetchInterval: 60_000 },
  );
}

const DATES = new Set(["dueDate", "expiryDate", "validUntil", "date", "executeAt"]);

/** Title and text of a notice: the same i18n resource as its email (`emails:<template or type>`), with dates in the user's format. */
export function noticeText(
  t: (key: string, params?: Record<string, string | number>) => string,
  formatDate: (iso: string) => string,
  item: InboxItem,
): { title: string; body: string } {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(item.params ?? {}))
    params[k] = DATES.has(k) && typeof v === "string" ? formatDate(v) : v;
  const tpl = typeof item.params?.["template"] === "string" ? item.params["template"] : item.type;
  const clean = (s: string) =>
    s
      .replace(/\{\{\w+\}\}/g, "")
      .replace(/\s+/g, " ")
      .trim();
  const subject = t(`emails:${tpl}.subject`, params);
  const body = t(`emails:${tpl}.body`, params);
  const missing = subject === `emails:${tpl}.subject`;
  return {
    title: missing ? t("ui:inbox.generic") : clean(subject),
    body: missing || body === `emails:${tpl}.body` ? "" : clean(body),
  };
}

/** The kind shown as the pill of a row (design 04 §7): what the notice is about. */
export type InboxKind = "review" | "removal" | "exception" | "account";
const KIND: Record<string, InboxKind> = {
  removal_requested: "removal",
  removal_scheduled: "removal",
  removal_cancelled: "removal",
  exception_to_approve: "exception",
  lifecycle: "account",
  permission_lost: "account",
  upgrade_needed: "account",
  admin_notice: "account",
};
export const kindOf = (type: string): InboxKind => KIND[type] ?? "review";

/** "2 hours ago" in the person's language; a date older than a month is left to the absolute format. */
export function relativeTime(iso: string, now: Date, locale: string): string | null {
  const diff = new Date(iso).getTime() - now.getTime();
  if (Number.isNaN(diff)) return null;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const min = Math.round(diff / 60_000);
  if (Math.abs(min) < 1) return rtf.format(0, "minute");
  if (Math.abs(min) < 60) return rtf.format(min, "minute");
  const h = Math.round(min / 60);
  if (Math.abs(h) < 24) return rtf.format(h, "hour");
  const d = Math.round(h / 24);
  return Math.abs(d) <= 30 ? rtf.format(d, "day") : null;
}

/** Types whose item can be postponed from the inbox (BR-107): the review reminders. */
export const SNOOZABLE = new Set(["review_started", "reminder", "overdue"]);
